import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ModalSkeleton from "../../../Modals/ModalSkeleton";
import CustomButton from "../../Button/CustomButton";
import { toast } from "react-toastify";
import { apiPatchRequest, apiPostRequest } from "../../../../helpers/Requests";
import { roleAccess } from "../../../../helpers/RolesPermission";
import { USER_ROLE } from "../../../../Constants/constant";
import { FiTarget, FiTrendingUp, FiFileText, FiAlertCircle } from "react-icons/fi";

const AddGoal = (props) => {
    const {
        open,
        handleClose,
        setOpen,
        participantInfo,
        onGoalAdded,
        selectedGoal,
        onGoalUpdated,
        onGoalDeleted
    } = props;

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: {
            goalTitle: "",
            goalType: "Short Term",
            progress: 0,
            achievementNotes: "",
            hurdles: "",
        },
    });
    const [loading, setLoading] = useState(false);
    const goalTypeOptions = ["Short Term", "Medium Term", "Long Term"];

    const goalTitle = watch("goalTitle");
    const achievementNotes = watch("achievementNotes");
    const hurdles = watch("hurdles");
    const progress = watch("progress");

    const modalFooter = () => (
        <div className="flex justify-end gap-3 px-1">
            <CustomButton
                clickHandler={() => {
                    setOpen(false);
                    if (!selectedGoal) {
                        reset();
                    } else {
                        setValue("goalTitle", selectedGoal.goal_title);
                        setValue("goalType", selectedGoal.goal_type);
                        setValue("progress", selectedGoal.progress);
                        setValue("achievementNotes", selectedGoal.achievement_notes);
                        setValue("hurdles", selectedGoal.hurdles);
                        clearErrors();
                    }
                }}
                label="Cancel"
                variant="secondary"
                isDisabled={loading}
                cssClass="!px-4 !py-1.5 text-sm"
            />
            <CustomButton
                showLoading={loading}
                isDisabled={loading}
                label={selectedGoal ? 'Update Goal' : 'Add Goal'}
                variant='primary'
                clickHandler={() => {
                    const buttonRef = document.getElementById('addGoalButtonModal');
                    if (buttonRef) {
                        buttonRef.click();
                    }
                }}
                cssClass="!px-4 !py-1.5 text-sm"
            />
        </div>
    );

    const addOrUpdateGoal = async (payload) => {
        setLoading(true);
        try {
            if (selectedGoal) {
                const response = await apiPatchRequest(`/sp/participant-goal/${selectedGoal.uuid}`, payload);
                if (response.status === 200) {
                    toast.success("Goal updated successfully!");
                    onGoalUpdated?.(response?.data?.data);
                }
            } else {
                if (roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) {
                    const response = await apiPostRequest(`/sp/participant-goal`, payload);
                    if (response?.status === 200 || response?.status === 201) {
                        toast.success("Goal added successfully!");
                        onGoalAdded?.(response?.data?.data);
                    }
                }
            }
        } catch (error) {
            setLoading(false);
            toast.error(error?.message ?? "Something went wrong");
        }
    };

    useEffect(() => {
        if (selectedGoal) {
            setValue("goalTitle", selectedGoal.goal_title || "");
            setValue("goalType", selectedGoal.goal_type || "Short Term");
            setValue("progress", selectedGoal.progress || 0);
            setValue("achievementNotes", selectedGoal.achievement_notes || "");
            setValue("hurdles", selectedGoal.hurdles || "");
        } else {
            reset();
        }
    }, [selectedGoal, setValue, reset]);

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const payload = {
                pt_id: participantInfo.id,
                goal_title: data.goalTitle,
                goal_type: data.goalType,
                progress: data.progress,
                achievement_notes: data.achievementNotes,
                hurdles: data.hurdles,
            };

            await addOrUpdateGoal(payload);
            setOpen(false);
            reset();
        } catch (error) {
            toast.error(error?.message ?? "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (progress === "" || progress === undefined || progress === null) {
            setValue("progress", 0);
        } else {
            const normalizedValue = Math.min(Math.max(parseInt(progress, 10) || 0, 0), 100);
            if (normalizedValue !== progress) {
                setValue("progress", normalizedValue);
            }
        }
    }, [progress, setValue]);

    const progressPercentage = progress || 0;

    return (
        <ModalSkeleton
            title={selectedGoal ? "Edit Goal" : "Add Goal"}
            cssClass='w-full max-w-xl'
            modalFooter={modalFooter()}
            isOpen={open}
            closeModal={() => {
                setOpen(false);
                if (!selectedGoal) {
                    reset();
                } else {
                    setValue("goalTitle", selectedGoal.goal_title || "");
                    setValue("goalType", selectedGoal.goal_type || "Short Term");
                    setValue("progress", selectedGoal.progress || 0);
                    setValue("achievementNotes", selectedGoal.achievement_notes || "");
                    setValue("hurdles", selectedGoal.hurdles || "");
                    clearErrors();
                }
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
                {/* Goal Description */}
                <div className="space-y-1.5">
                    <label
                        htmlFor='goalTitle'
                        className='flex items-center gap-1.5 text-sm font-semibold text-gray-800'
                    >
                        <FiTarget className="h-3.5 w-3.5 text-blue-600" />
                        <span>Describe the Goal</span>
                        <span className='text-red-500'>*</span>
                    </label>
                    <div className="relative">
                        <textarea
                            id="goalTitle"
                            rows={2}
                            placeholder='Enter a clear and measurable goal description...'
                            className={`w-full px-3 py-2 pb-10 pr-16 text-sm border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all resize-none ${
                                errors?.goalTitle 
                                    ? 'border-red-300 bg-red-50/50 focus:ring-red-500 focus:border-red-500' 
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300 focus:ring-blue-500 focus:border-blue-500 focus:bg-white'
                            }`}
                            {...register("goalTitle", {
                                required: {
                                    value: true,
                                    message: 'Goal description is required'
                                },
                                validate: (value) => value.trim() !== '' || 'Goal description cannot be empty or contain only spaces',
                                maxLength: {
                                    value: 1500,
                                    message: 'Goal description cannot exceed 1500 characters'
                                }
                            })}
                        />
                        <div className="absolute bottom-2 right-2">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                (goalTitle?.length || 0) > 1300 
                                    ? 'text-orange-700 bg-orange-100' 
                                    : (goalTitle?.length || 0) > 0
                                    ? 'text-gray-600 bg-gray-200'
                                    : 'text-gray-400 bg-gray-100'
                            }`}>
                                {(goalTitle?.length || 0)} / 1500
                            </span>
                        </div>
                    </div>
                    {errors?.goalTitle && (
                        <p className='text-xs text-red-600 flex items-center gap-1'>
                            <FiAlertCircle className="h-3 w-3" />
                            {errors?.goalTitle.message}
                        </p>
                    )}
                </div>

                {/* Goal Type and Progress Row */}
                <div className="grid grid-cols-2 gap-3">
                    {/* Goal Type */}
                    <div className="space-y-1.5">
                        <label
                            htmlFor='goalType'
                            className='flex items-center gap-1.5 text-sm font-semibold text-gray-800'
                        >
                            <FiFileText className="h-3.5 w-3.5 text-blue-600" />
                            <span>Goal Type</span>
                        </label>
                        <select
                            id="goalType"
                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all appearance-none pr-8 cursor-pointer"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                                backgroundSize: '1em 1em',
                                backgroundPosition: 'right 0.75rem center',
                                backgroundRepeat: 'no-repeat'
                            }}
                            {...register("goalType")}
                        >
                            {goalTypeOptions.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Progress */}
                    <div className="space-y-1.5">
                        <label
                            htmlFor='progress'
                            className='flex items-center gap-1.5 text-sm font-semibold text-gray-800'
                        >
                            <FiTrendingUp className="h-3.5 w-3.5 text-blue-600" />
                            <span>Progress</span>
                        </label>
                        <div className="space-y-1.5">
                            <div className="relative">
                                <input
                                    id="progress"
                                    type='number'
                                    min={0}
                                    max={100}
                                    className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all ${
                                        errors?.progress 
                                            ? 'border-red-300 bg-red-50/50 focus:ring-red-500 focus:border-red-500' 
                                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 focus:ring-blue-500 focus:border-blue-500 focus:bg-white'
                                    }`}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        if (value === "") {
                                            setValue("progress", 0);
                                            return;
                                        }
                                        const numeric = parseInt(value, 10);
                                        const normalizedValue = Math.min(Math.max(numeric || 0, 0), 100);
                                        setValue("progress", normalizedValue);
                                    }}
                                    {...register("progress", {
                                        min: { value: 0, message: "Min 0%" },
                                        max: { value: 100, message: "Max 100%" },
                                    })}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500">
                                    %
                                </span>
                            </div>
                            {/* Compact Progress Bar */}
                            <div className="space-y-1">
                                <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300 ease-out rounded-full"
                                        style={{ width: `${progressPercentage}%` }}
                                    />
                                </div>
                                <p className="text-xs text-gray-600 text-right">{progressPercentage}%</p>
                            </div>
                            {errors?.progress && (
                                <p className='text-xs text-red-600 flex items-center gap-1'>
                                    <FiAlertCircle className="h-3 w-3" />
                                    {errors?.progress.message}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Achievement Notes */}
                <div className="space-y-1.5">
                    <label
                        htmlFor='achievementNotes'
                        className='flex items-center gap-1.5 text-sm font-semibold text-gray-800'
                    >
                        <FiTrendingUp className="h-3.5 w-3.5 text-green-600" />
                        <span>Achievement Notes</span>
                    </label>
                    <div className="relative">
                        <textarea
                            id="achievementNotes"
                            {...register("achievementNotes", {
                                maxLength: {
                                    value: 1500,
                                    message: 'Achievement notes cannot exceed 1500 characters',
                                }
                            })}
                            placeholder="Document key milestones and achievements..."
                            className={`w-full px-3 py-2 pb-10 pr-16 text-sm border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all resize-none ${
                                errors?.achievementNotes 
                                    ? 'border-red-300 bg-red-50/50 focus:ring-red-500 focus:border-red-500' 
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300 focus:ring-green-500 focus:border-green-500 focus:bg-white'
                            }`}
                            rows={2}
                        />
                        <div className="absolute bottom-2 right-2">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                (achievementNotes?.length || 0) > 1300 
                                    ? 'text-orange-700 bg-orange-100' 
                                    : (achievementNotes?.length || 0) > 0
                                    ? 'text-gray-600 bg-gray-200'
                                    : 'text-gray-400 bg-gray-100'
                            }`}>
                                {(achievementNotes?.length || 0)} / 1500
                            </span>
                        </div>
                    </div>
                    {errors?.achievementNotes && (
                        <p className='text-xs text-red-600 flex items-center gap-1'>
                            <FiAlertCircle className="h-3 w-3" />
                            {errors?.achievementNotes.message}
                        </p>
                    )}
                </div>

                {/* Hurdles */}
                <div className="space-y-1.5">
                    <label
                        htmlFor='hurdles'
                        className='flex items-center gap-1.5 text-sm font-semibold text-gray-800'
                    >
                        <FiAlertCircle className="h-3.5 w-3.5 text-orange-600" />
                        <span>Hurdles</span>
                    </label>
                    <div className="relative">
                        <textarea
                            id="hurdles"
                            {...register("hurdles", {
                                maxLength: {
                                    value: 1500,
                                    message: 'Hurdles cannot exceed 1500 characters',
                                }
                            })}
                            placeholder="Note any challenges or obstacles encountered..."
                            className={`w-full px-3 py-2 pb-10 pr-16 text-sm border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all resize-none ${
                                errors?.hurdles 
                                    ? 'border-red-300 bg-red-50/50 focus:ring-red-500 focus:border-red-500' 
                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300 focus:ring-orange-500 focus:border-orange-500 focus:bg-white'
                            }`}
                            rows={2}
                        />
                        <div className="absolute bottom-2 right-2">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                (hurdles?.length || 0) > 1300 
                                    ? 'text-orange-700 bg-orange-100' 
                                    : (hurdles?.length || 0) > 0
                                    ? 'text-gray-600 bg-gray-200'
                                    : 'text-gray-400 bg-gray-100'
                            }`}>
                                {(hurdles?.length || 0)} / 1500
                            </span>
                        </div>
                    </div>
                    {errors?.hurdles && (
                        <p className='text-xs text-red-600 flex items-center gap-1'>
                            <FiAlertCircle className="h-3 w-3" />
                            {errors?.hurdles.message}
                        </p>
                    )}
                </div>

                {/* Hidden Submit Button */}
                <div className='hidden'>
                    <button
                        type='submit'
                        id='addGoalButtonModal'
                    />
                </div>
            </form>
        </ModalSkeleton>
    );
};

export default AddGoal;
